import React from "react";
import PropTypes from "prop-types";

// Generic, controlled date-range picker (two <input type="date">). No
// domain knowledge — the caller decides what the range filters.
//
// Prop API:
//   - from: string ("" | "yyyy-mm-dd") — current "from" value
//   - to: string ("" | "yyyy-mm-dd") — current "to" value
//   - onChange: (next: { from: string, to: string }) => void
//       Called once per edit with the full next {from, to} pair — this
//       component holds no internal date state. Changing "from" to a date
//       later than the current "to" clears "to" in the same call (mirrors
//       the legacy action-needed behavior at
//       src/pages/action-needed/index.jsx:149), so the caller receives an
//       already-consistent pair without extra logic.
//   - fromLabel / toLabel: optional label overrides (default "From"/"To")
//   - idPrefix: optional prefix for the generated input ids used to wire
//       each <label>'s htmlFor (default "date-range"); pass a unique value
//       if a page ever renders more than one DateRangeFilter at once
//
// The "to" input is disabled until "from" is set, and its `min` is pinned
// to "from" once chosen, so a "to" earlier than "from" can't be picked.
function DateRangeFilter({ from, to, onChange, fromLabel, toLabel, idPrefix }) {
    const handleFromChange = (event) => {
        const nextFrom = event.target.value;
        const nextTo = to && nextFrom && to < nextFrom ? "" : to;
        onChange({ from: nextFrom, to: nextTo });
    };

    const handleToChange = (event) => {
        onChange({ from, to: event.target.value });
    };

    // Verbatim from the legacy order page's own date inputs. The disabled
    // treatment is legacy's `disabled:opacity-50` rather than a separate
    // fill/text pair, so the "to" input's disabled state reads the same here
    // as it does on /all-orders.
    const inputClassName =
        "border border-gray-300 rounded-lg px-3 py-2 text-sm font-sans focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed";
    const fromId = `${idPrefix}-from`;
    const toId = `${idPrefix}-to`;

    return (
        <div className="flex flex-wrap gap-4">
            <div>
                <label htmlFor={fromId} className="mb-1 block text-sm font-bold font-sans text-black">
                    {fromLabel}
                </label>
                <input id={fromId} type="date" value={from} onChange={handleFromChange} className={inputClassName} />
            </div>
            <div>
                <label htmlFor={toId} className="mb-1 block text-sm font-bold font-sans text-black">
                    {toLabel}
                </label>
                <input
                    id={toId}
                    type="date"
                    value={to}
                    min={from || undefined}
                    disabled={!from}
                    onChange={handleToChange}
                    className={inputClassName}
                />
            </div>
        </div>
    );
}

DateRangeFilter.propTypes = {
    from: PropTypes.string,
    to: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    fromLabel: PropTypes.node,
    toLabel: PropTypes.node,
    idPrefix: PropTypes.string,
};

DateRangeFilter.defaultProps = {
    from: "",
    to: "",
    fromLabel: "From",
    toLabel: "To",
    idPrefix: "date-range",
};

export default DateRangeFilter;
